/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { Component } from 'react';
import { Tag } from 'antd';
import { get } from 'lodash';
import { SelectValue } from 'antd/lib/select';
import colors from '../colors';
import { AutoComplete } from '../autocomplete';

interface IProps {
  // tslint:disable-next-line: no-any
  dataSource: any[];
  placeholder?: string;
  onTagsChange?: (selected: string[]) => void;
  ref?: (comp: MultiSelectSearchComponent) => void;
  disabled?: boolean;
  selected?: string[];
  limitReached?: boolean;
  hide: string[];
}

type Selected = { text: string; value: string };

/* AER-1696: resolve the friendly label for a raw option value. `dataSource`
   entries are either plain strings (value === label) or `{ text, value }`
   pairs (e.g. Add Service Record, where `value` is the catalog key
   `comp_desdryers` and `text` is "Desiccant Tower"). Returns undefined when the
   value isn't (or is no longer) in the list. */
const textFromDataSource = (dataSource: IProps['dataSource'], value: string) => {
  const match = (dataSource || []).find(d => `${get(d, 'value', d)}` === value);
  const text = match === undefined ? undefined : get(match, 'text', match);
  return typeof text === 'string' && text.length > 0 ? text : undefined;
};

/* AER-1696: antd v3 handed `onSelect` a React element whose `props.children`
   was the rendered label. antd v6 (rc-select) hands over a plain option object
   whose label sits on `children` (options built from `dataSource`/`<Option>`)
   or on `label` (the `options={[...]}` API). It still exposes a legacy `props`
   getter that returns the option itself, but reading through it logs a dev
   warning — so try the modern shapes first and keep `props.children` last. */
const labelFromOption = (option: Object) => {
  // `unknown[]`, not the inferred element type: `option` is typed `Object`, so
  // lodash resolves these `get` calls to `never` and the `typeof` guard below
  // would have nothing to narrow.
  const candidates: unknown[] = [
    get(option, 'children'),
    get(option, 'label'),
    get(option, 'props.children'),
  ];
  return candidates.find(c => typeof c === 'string' && c.length > 0) as string | undefined;
};

/* AER-1696: build a chip from whatever the `selected` prop holds — a raw value
   string, or a `{ value, text }` pair. Text already captured for that value
   (by `onSelect`, or by a previous pass) is preserved, so a re-render driven by
   the parent form can no longer downgrade a chip back to its raw value/key.
   That downgrade was the actual defect: `componentDidUpdate` rebuilt `_selected`
   straight from the value list every time the `selected` prop changed identity
   (which happens on the very next render after a selection, because
   `onTagsChange` writes the new array into the form). */
const toSelected = (dataSource: IProps['dataSource'], known: Selected[], entry: string): Selected => {
  const value = `${get(entry, 'value', entry)}`;
  const explicit = get(entry, 'text', undefined);
  const rememberedEntry = known.find(k => k.value === value);
  const remembered = rememberedEntry ? rememberedEntry.text : undefined;
  const text =
    (typeof explicit === 'string' && explicit.length > 0 ? explicit : undefined) ||
    (typeof remembered === 'string' && remembered.length > 0 ? remembered : undefined) ||
    textFromDataSource(dataSource, value) ||
    value;
  return { value, text };
};

interface IState {
  value: string;
  selected: string[];
  _selected: Selected[];
}

export class MultiSelectSearchComponent extends Component<IProps, IState> {
  static defaultProps: IProps;
  state: IState = {
    value: '',
    selected: this.props.selected || [],
    _selected: (this.props.selected || []).map(s =>
      toSelected(this.props.dataSource, [], s)
    ),
  };
  tagChange = () => {
    if (this.props.onTagsChange) {
      this.props.onTagsChange(this.state.selected);
    }
  };
  onSelect = (value: SelectValue, option: Object) => {
    setTimeout(() => {
      if (this.props.limitReached || !value) return;
      const state = { ...this.state };
      state.value = '';
      state.selected = [...state.selected, value as string] as string[];
      state._selected = [
        ...state._selected,
        {
          value: value.toString(),
          // AER-1696: friendly label from the option, falling back to a
          // lookup in `dataSource` and finally to the raw value.
          text:
            labelFromOption(option) ||
            textFromDataSource(this.props.dataSource, value.toString()) ||
            value.toString(),
        },
      ];
      this.setState(state, this.tagChange);
    }, 10);
  };
  removeSelected = (removedTag: string) => {
    const { _selected } = this.state;

    const _selectedUpdated = _selected.filter(tag => tag.value !== removedTag);
    const selectedUpdated = _selectedUpdated.map(s => s.value);
    this.setState(
      {
        _selected: _selectedUpdated,
        selected: selectedUpdated,
      },
      this.tagChange
    );
  };
  onChange = (value: SelectValue) => {
    this.setState({ value: value as string });
  };
  clearTags = () => {
    this.setState({ selected: [], _selected: [] });
  };
  componentDidMount = () => {
    if (this.props.ref) {
      this.props.ref(this);
    }
  };
  /* AER-1716: this used to branch on a `useSelectedState` prop that was meant
     to keep the locally-held chips instead of rebuilding them. It never ran —
     `defaultProps` declared the transposed `useStateSelected`, so the prop was
     permanently undefined and no caller passed either name. Enabling it would
     have broken the AER-1601 revert-on-cancel path (Add Service Record pushes
     the persisted list back through `selected`), so the dead branch and its
     prop were removed rather than repaired. Both halves below now derive from
     the same source, which is what the false branch always did. */
  componentDidUpdate(op: IProps) {
    if (op.selected !== this.props.selected) {
      const _selected = (this.props.selected || this.state.selected).map(s =>
        toSelected(this.props.dataSource, this.state._selected, s)
      );
      this.setState({
        selected: this.props.selected || this.state.selected,
        _selected,
      });
    }
  }
  render() {
    const { value, _selected, selected } = this.state;
    const { placeholder, disabled, dataSource, limitReached, hide } = this.props;
    const sel = selected.map(s => get(s, 'value', s));
    const _dataSource = dataSource.filter(data => {
      const dv = get(data, 'value', data);
      return !sel.includes(`${dv}`) && !hide.includes(`${dv}`);
    });
    const props = {
      dataSource: _dataSource,
      placeholder,
      value,
      onSelect: this.onSelect,
      onChange: this.onChange,
      disabled: disabled || limitReached,
    };

    return (
      <div
        css={css`
          width: 100%;
        `}
      >
        <AutoComplete
          css={css`
            width: 100%;
          `}
          {...props}
          filterOption={(inputValue, option) => {
            return (
              `${get(option, 'props.children', '')}`
                .toUpperCase()
                .indexOf(inputValue.toUpperCase()) !== -1
            );
          }}
        />
        <div
          css={css`
            margin-top: 5px;
          `}
        >
          {_selected.map(sel => {
            return (
              <Tag
                css={css`
                  margin-right: 8px;
                  margin-bottom: 5px;
                `}
                color={colors.tagDark}
                /* AER-1465: antd v6 defaults to variant="filled" which renders
                   custom hex colors as a *very pale* background with the hex
                   used as TEXT color — the opposite of v3's behaviour where
                   color="#4d4d4d" produced a dark filled chip with white text.
                   Force variant="solid" so the chip looks like the v3 design.
                   v6 also dropped the default margin-right that v3 applied to
                   Tag, so add it back here to keep chips visually separated. */
                variant="solid"
                key={sel.value}
                closable={!disabled}
                onClose={() => this.removeSelected(sel.value)}
              >
                {`${get(sel, 'text')}`}
              </Tag>
            );
          })}
        </div>
      </div>
    );
  }
}

MultiSelectSearchComponent.defaultProps = {
  placeholder: 'Search...',
  dataSource: [{ text: '', value: '' }],
  onTagsChange: () => 0,
  hide: [],
} as IProps;
